/**
 * Planned vs Actual on an Active Resource Plan — pure, integer-only, no I/O.
 *
 * Actual hours arrive from the NOS time-entry sync (`hours_entry`), bucketed per Resource
 * per month. NOS sends hours and a billable flag only — no rate, no cost, no Labor
 * Category — so every dollar on the actual side is priced with the PLAN's own rates
 * (workspace spec D6):
 *
 *   actual revenue = actual BILLABLE hours × the line's bill rate
 *   actual cost    = actual hours (billable or not) × the Labor Category's internal cost
 *                    — or, in any month the Resource has one, their own Internal Rate
 *                    (2026-10-14, docs/specs/resource-internal-rates.md), on both the
 *                    planned and the actual side, so the variance compares like with like
 *
 * Units are the same as src/lib/resource-plan-math.ts: hours in centi, money in Micros,
 * percentages in bp. Planned hours come from `expandLineCells`, so ramp and SteadyState
 * expansion are applied exactly as the grid and summary strip apply them.
 *
 * ──────────────────────────────────────────────────────────────────────────────
 * THE WINDOW
 * ──────────────────────────────────────────────────────────────────────────────
 * Compared figures cover the plan's months up to and including `asOfMonth` (the current
 * month, month-to-date). Planned figures are clipped to the same window so the variance
 * compares like with like; full-plan planned totals are reported alongside. Actual hours
 * logged outside the window are not compared — they are counted in `outsideWindowHoursCenti`
 * so they are never silently lost.
 *
 * ──────────────────────────────────────────────────────────────────────────────
 * WHO GETS WHICH HOURS
 * ──────────────────────────────────────────────────────────────────────────────
 * A Resource's hours go to the Plan Lines naming them as Suggested Resource — confirmed
 * lines only when they hold any confirmed line, otherwise every line suggesting them.
 * Across several lines, each month's hours split in proportion to that month's planned
 * hours, equally when nothing is planned; remainders go largest-first so the split sums
 * exactly to what was logged.
 *
 * A Resource on no line is "Not on plan" (spec R2). They are priced at the Labor Category
 * inferred from their Pod Assignment's Discipline where there is one — that category's
 * internal cost, and the plan's bill rate for it if the plan prices it, else the plan's
 * blended rate — and at the plan's blended rate and blended cost otherwise. Every row says
 * which (`rateSource`), because an inferred price is a statement the reader must be able
 * to discount.
 */

import { effectivePlanStatus } from "@/lib/resource-plan-lifecycle";
import { type Bp, FULL_PERCENT_BP, type Micros, marginBp, parseMinor } from "./rate-card";
import { divideRoundHalfUp, expandLineCells, parseRampMap } from "./resource-plan-math";

/** Hours × 100. */
type HoursCenti = number;

/** Micros per cent — the reduction before any × FULL_PERCENT_BP (see resource-plan-math). */
const MICROS_PER_CENT = 10_000;
/** Micros per (rate cent) — a bill rate in cents × 1e4 is Micros per hour. */
const MICROS_PER_RATE_CENT = 10_000;

/** month → Internal Rate in Micros per hour. */
function parseCostByMonth(
  costByMonth: ReadonlyMap<string, string> | undefined,
): Map<string, Micros> {
  const out = new Map<string, Micros>();
  for (const [month, cost] of costByMonth ?? []) {
    out.set(month, parseMinor(cost, 2) * MICROS_PER_RATE_CENT);
  }
  return out;
}

/**
 * Which plans carry an Actuals tab: those in delivery. Active per the original spec, plus
 * Won plans, and any plan the RM has marked Staffed on the Resourcing flag — product
 * decision 2026-10-12 (workspace spec, Resolved questions). Staffed is a legacy status
 * (retired 2026-10-14) kept for any row still holding it; a Locked plan answers with the
 * status it was locked from. The server gate and the tab both read this, so they cannot
 * disagree.
 */
const ACTUALS_PLAN_STATUSES: ReadonlySet<string> = new Set(["Won", "Staffed", "Active"]);

export function planShowsActuals(plan: {
  status: string;
  lockedFromStatus?: string | null;
  resourcingStatus?: string | null;
}): boolean {
  return (
    ACTUALS_PLAN_STATUSES.has(effectivePlanStatus(plan)) || plan.resourcingStatus === "Staffed"
  );
}

/**
 * RP-24 — a split line's planned cost rate per month (Micros/hr): Σ(Share hours × that
 * holder's rate), with hours the Shares leave uncovered at the Labor Category cost, over
 * the line's hours (or the Shares', when they exceed it). Months with no Share hours are
 * absent, so the Labor Category cost applies to them.
 */
function blendedShareCostByMonth(
  holders: NonNullable<PlanActualsLineInput["shareHolders"]>,
  lineEnteredByMonth: ReadonlyMap<string, HoursCenti>,
  laborCostMicros: Micros,
): Map<string, Micros> {
  const rates = holders.map((h) => parseCostByMonth(h.costByMonth));
  const months = new Set<string>();
  for (const h of holders) for (const m of h.enteredCentiByMonth.keys()) months.add(m);
  const out = new Map<string, Micros>();
  for (const month of months) {
    let covered = 0;
    let weighted = 0;
    holders.forEach((h, i) => {
      const centi = h.enteredCentiByMonth.get(month) ?? 0;
      covered += centi;
      weighted += centi * (rates[i]?.get(month) ?? laborCostMicros);
    });
    const lineCenti = lineEnteredByMonth.get(month) ?? 0;
    weighted += Math.max(0, lineCenti - covered) * laborCostMicros;
    const denominator = Math.max(lineCenti, covered);
    if (denominator > 0) out.set(month, divideRoundHalfUp(weighted, denominator));
  }
  return out;
}

// ── Inputs ────────────────────────────────────────────────────────────────────

export interface PlanActualsLineInput {
  id: string;
  laborCategoryId: string;
  laborCategoryName: string;
  /** `plan_line.suggested_resource_id`. */
  resourceId: string | null;
  resourceName: string | null;
  /** `plan_line.suggested_resource_confirmed`. */
  confirmed: boolean;
  /** `numeric(10,2)` strings. */
  billRate: string;
  avgInternalCost: string;
  months: ReadonlyArray<{ month: string; hours: string }>;
  steadyStateHours?: string | null;
  /** The Suggested Resource's Internal Rate per month — see PlanLineInput.costByMonth. */
  costByMonth?: ReadonlyMap<string, string>;
  /**
   * RP-24 — a SPLIT line's Share holders (its `resourceId` is then null). Each holder's
   * actual hours land on this line, split across their lines by their OWN planned hours that
   * month and costed at their own Internal Rate (or the Labor Category cost). The line's
   * planned cost per month is the hour-weighted blend across them.
   */
  shareHolders?: ReadonlyArray<{
    resourceId: string;
    /** month → the Share's ENTERED hours (centi). */
    enteredCentiByMonth: ReadonlyMap<string, HoursCenti>;
    costByMonth?: ReadonlyMap<string, string>;
  }>;
}

/** Actual hours for one Resource in one month (`YYYY-MM-01`). */
export interface ActualHoursInput {
  resourceId: string;
  month: string;
  hoursCenti: HoursCenti;
  billableHoursCenti: HoursCenti;
}

/** What is known about a Resource who logged time but is on no Plan Line. */
export interface UnplannedResourceInput {
  name: string;
  /** Inferred from their Pod Assignment's Discipline's default Labor Category. */
  laborCategory: { id: string; name: string; avgInternalCost: string } | null;
  /** Their own Internal Rate per month; a month present here prices their cost. */
  costByMonth?: ReadonlyMap<string, string>;
}

export interface PlanActualsInput {
  pricingModel: "TimeAndMaterials" | "FixedPrice";
  /** `resource_plan.contracted_fee`, or null. */
  contractedFee: string | null;
  /** Every month of the plan, `YYYY-MM-01`, in order. */
  planMonths: readonly string[];
  entryMode?: "MonthByMonth" | "SteadyState";
  rampByMonth?: ReadonlyMap<string, string>;
  /** The last month compared — the current month, month-to-date. `YYYY-MM-01`. */
  asOfMonth: string;
  lines: readonly PlanActualsLineInput[];
  actuals: readonly ActualHoursInput[];
  /** Keyed by Resource id. A Resource absent here is shown with no name or category. */
  unplannedResources: ReadonlyMap<string, UnplannedResourceInput>;
}

// ── Outputs ───────────────────────────────────────────────────────────────────

/**
 * - `line` — the Plan Line's own bill rate and its Labor Category's cost.
 * - `plan-category` — a Not-on-plan Resource priced at the plan's rate for their inferred category.
 * - `category-blended` — inferred category's cost, but the plan does not price that
 *   category, so the plan's blended bill rate.
 * - `blended` — no category could be inferred: the plan's blended rate and blended cost.
 * - `unpriced` — the plan has no hours to blend from; dollars are zero.
 */
export type ActualsRateSource =
  | "line"
  | "plan-category"
  | "category-blended"
  | "blended"
  | "unpriced";

export interface Variance {
  /** actual − planned, in the figure's own unit. Positive = over plan. */
  amount: number;
  /** amount ÷ planned, in bp. Null when nothing was planned. */
  bp: Bp | null;
}

export interface PlannedFigures {
  hoursCenti: HoursCenti;
  revenueMicros: Micros;
  costMicros: Micros;
  marginBp: Bp | null;
}

export interface ActualFigures extends PlannedFigures {
  billableHoursCenti: HoursCenti;
}

export interface ComparedFigures {
  planned: PlannedFigures;
  actual: ActualFigures;
  variance: {
    hours: Variance;
    revenue: Variance;
    cost: Variance;
    /** actual margin − planned margin, in bp (percentage points × 100). */
    marginBp: Bp | null;
  };
}

export interface PlanActualsRow extends ComparedFigures {
  /** Line id, or `resource:<id>` for a Not-on-plan row. */
  key: string;
  kind: "line" | "unplanned";
  lineId: string | null;
  laborCategoryId: string | null;
  laborCategoryName: string | null;
  resourceId: string | null;
  resourceName: string | null;
  rateSource: ActualsRateSource;
  /** Applied rates, Micros per hour. Null when unpriced. */
  billRateMicros: Micros | null;
  /** The cost rate in effect at the end of the window. */
  costRateMicros: Micros | null;
  /**
   * Whether any compared month was priced at the Resource's own Internal Rate. The API
   * shaper withholds this row's cost figures from anyone without viewResourceInternalRates.
   */
  costSource: "labor_category" | "resource";
}

export interface PlanActualsMonth extends ComparedFigures {
  month: string;
}

export interface PlanActualsResult {
  /** The compared months, in order — plan start through min(asOfMonth, plan end). */
  windowMonths: string[];
  /** True when `asOfMonth` is inside the window, so its figures are month-to-date. */
  includesPartialMonth: boolean;
  rows: PlanActualsRow[];
  byMonth: PlanActualsMonth[];
  summary: ComparedFigures & {
    /** The whole plan as planned, regardless of window — for reference. */
    fullPlan: PlannedFigures;
    /** Hours logged on the Solution in months outside the window. Not compared. */
    outsideWindowHoursCenti: HoursCenti;
    /** Fixed Price only: actual cost to date against the Contracted Fee. */
    fixedPriceBurn: {
      contractedFeeMicros: Micros;
      actualCostMicros: Micros;
      burnBp: Bp | null;
    } | null;
  };
}

// ── Helpers ───────────────────────────────────────────────────────────────────

/**
 * Rate (Micros per hour) × centi-hours → Micros. A whole-cent rate takes the same
 * `cents × centi × 100` path as resource-plan-math, which keeps 100× more headroom below
 * 2^53 than multiplying the Micros rate directly; only a blended (sub-cent) rate needs the
 * division.
 */
function moneyFor(rateMicros: Micros, hoursCenti: HoursCenti): Micros {
  if (rateMicros % MICROS_PER_RATE_CENT === 0) {
    const out = (rateMicros / MICROS_PER_RATE_CENT) * hoursCenti * 100;
    if (!Number.isSafeInteger(out))
      throw new RangeError(`money exceeded exact integer range: ${out}`);
    return out;
  }
  return divideRoundHalfUp(rateMicros * hoursCenti, 100);
}

function marginOf(revenueMicros: Micros, costMicros: Micros): Bp | null {
  if (revenueMicros === 0) return null;
  return marginBp(
    divideRoundHalfUp(revenueMicros, MICROS_PER_CENT),
    divideRoundHalfUp(costMicros, MICROS_PER_CENT),
  );
}

/** Variance of two same-unit figures. Money is reduced to cents before × 1e4. */
export function varianceOf(planned: number, actual: number, unit: "hours" | "money"): Variance {
  const amount = actual - planned;
  if (planned === 0) return { amount, bp: null };
  const scale = unit === "money" ? MICROS_PER_CENT : 1;
  const p = divideRoundHalfUp(planned, scale);
  if (p === 0) return { amount, bp: null };
  return { amount, bp: divideRoundHalfUp(divideRoundHalfUp(amount, scale) * FULL_PERCENT_BP, p) };
}

/**
 * Split `total` across `weights` proportionally, largest remainder first, so the parts
 * always sum to `total` exactly. All-zero weights split equally.
 */
export function splitProportionally(total: number, weights: readonly number[]): number[] {
  if (weights.length === 0) return [];
  const sumW = weights.reduce((a, b) => a + b, 0);
  const w = sumW === 0 ? weights.map(() => 1) : weights;
  const denom = sumW === 0 ? weights.length : sumW;
  const sign = total < 0 ? -1 : 1;
  const abs = Math.abs(total);
  const parts = w.map((wi) => Math.floor((abs * wi) / denom));
  let remainder = abs - parts.reduce((a, b) => a + b, 0);
  const order = w
    .map((wi, i) => ({ i, frac: (abs * wi) % denom }))
    .sort((a, b) => b.frac - a.frac || a.i - b.i);
  for (const { i } of order) {
    if (remainder === 0) break;
    parts[i] = (parts[i] ?? 0) + 1;
    remainder -= 1;
  }
  return parts.map((p) => p * sign);
}

interface Accum {
  plannedHours: number;
  plannedRevenue: number;
  plannedCost: number;
  actualHours: number;
  actualBillable: number;
  actualRevenue: number;
  actualCost: number;
}

function emptyAccum(): Accum {
  return {
    plannedHours: 0,
    plannedRevenue: 0,
    plannedCost: 0,
    actualHours: 0,
    actualBillable: 0,
    actualRevenue: 0,
    actualCost: 0,
  };
}

function addInto(target: Accum, src: Accum): void {
  target.plannedHours += src.plannedHours;
  target.plannedRevenue += src.plannedRevenue;
  target.plannedCost += src.plannedCost;
  target.actualHours += src.actualHours;
  target.actualBillable += src.actualBillable;
  target.actualRevenue += src.actualRevenue;
  target.actualCost += src.actualCost;
}

function compare(a: Accum): ComparedFigures {
  const plannedMargin = marginOf(a.plannedRevenue, a.plannedCost);
  const actualMargin = marginOf(a.actualRevenue, a.actualCost);
  return {
    planned: {
      hoursCenti: a.plannedHours,
      revenueMicros: a.plannedRevenue,
      costMicros: a.plannedCost,
      marginBp: plannedMargin,
    },
    actual: {
      hoursCenti: a.actualHours,
      billableHoursCenti: a.actualBillable,
      revenueMicros: a.actualRevenue,
      costMicros: a.actualCost,
      marginBp: actualMargin,
    },
    variance: {
      hours: varianceOf(a.plannedHours, a.actualHours, "hours"),
      revenue: varianceOf(a.plannedRevenue, a.actualRevenue, "money"),
      cost: varianceOf(a.plannedCost, a.actualCost, "money"),
      marginBp:
        plannedMargin === null || actualMargin === null ? null : actualMargin - plannedMargin,
    },
  };
}

// ── The computation ───────────────────────────────────────────────────────────

export function computePlanActuals(input: PlanActualsInput): PlanActualsResult {
  const windowMonths = input.planMonths.filter((m) => m <= input.asOfMonth);
  const inWindow = new Set(windowMonths);
  const includesPartialMonth = inWindow.has(input.asOfMonth);
  const rampMilliByMonth = parseRampMap(input.rampByMonth);

  // ── Planned, per line per month (effective hours) ──
  interface LineState {
    line: PlanActualsLineInput;
    billRateMicros: Micros;
    costRateMicros: Micros;
    /** The Suggested Resource's Internal Rate by month (Micros/hr). */
    resourceCostByMonth: Map<string, Micros>;
    plannedByMonth: Map<string, HoursCenti>;
    fullPlanHours: HoursCenti;
    accum: Accum;
    accumByMonth: Map<string, Accum>;
  }
  const lineStates: LineState[] = input.lines.map((line) => {
    const cells = expandLineCells({
      line,
      months: input.planMonths,
      entryMode: input.entryMode,
      rampMilliByMonth,
    });
    const plannedByMonth = new Map<string, HoursCenti>();
    const enteredByMonth = new Map<string, HoursCenti>();
    let fullPlanHours = 0;
    for (const c of cells) {
      plannedByMonth.set(c.month, (plannedByMonth.get(c.month) ?? 0) + c.hoursCenti);
      enteredByMonth.set(c.month, (enteredByMonth.get(c.month) ?? 0) + c.enteredCenti);
      fullPlanHours += c.hoursCenti;
    }
    const costRateMicros = parseMinor(line.avgInternalCost, 2) * MICROS_PER_RATE_CENT;
    return {
      line,
      billRateMicros: parseMinor(line.billRate, 2) * MICROS_PER_RATE_CENT,
      costRateMicros,
      resourceCostByMonth:
        // Only when a holder actually prices at an Internal Rate — otherwise the blend IS
        // the Labor Category cost, and the line must still read as Labor Category-priced.
        line.shareHolders?.some((h) => (h.costByMonth?.size ?? 0) > 0) === true
          ? blendedShareCostByMonth(line.shareHolders, enteredByMonth, costRateMicros)
          : parseCostByMonth(line.costByMonth),
      plannedByMonth,
      fullPlanHours,
      accum: emptyAccum(),
      accumByMonth: new Map(),
    };
  });

  const lineCostFor = (s: LineState, month: string): Micros =>
    s.resourceCostByMonth.get(month) ?? s.costRateMicros;

  const fullPlan = emptyAccum();
  for (const s of lineStates) {
    fullPlan.plannedHours += s.fullPlanHours;
    fullPlan.plannedRevenue += moneyFor(s.billRateMicros, s.fullPlanHours);
    if (s.resourceCostByMonth.size === 0) {
      fullPlan.plannedCost += moneyFor(s.costRateMicros, s.fullPlanHours);
    } else {
      for (const [month, h] of s.plannedByMonth)
        fullPlan.plannedCost += moneyFor(lineCostFor(s, month), h);
    }
    for (const month of windowMonths) {
      const h = s.plannedByMonth.get(month) ?? 0;
      if (h === 0) continue;
      const slice = emptyAccum();
      slice.plannedHours = h;
      slice.plannedRevenue = moneyFor(s.billRateMicros, h);
      slice.plannedCost = moneyFor(lineCostFor(s, month), h);
      addInto(s.accum, slice);
      const m = s.accumByMonth.get(month) ?? emptyAccum();
      addInto(m, slice);
      s.accumByMonth.set(month, m);
    }
  }

  // Blended rates for pricing Not-on-plan Resources — revenue ÷ hours, never a mean of
  // rates (RP-6.4).
  const blendedBill =
    fullPlan.plannedHours === 0
      ? null
      : divideRoundHalfUp(fullPlan.plannedRevenue * 100, fullPlan.plannedHours);
  const blendedCost =
    fullPlan.plannedHours === 0
      ? null
      : divideRoundHalfUp(fullPlan.plannedCost * 100, fullPlan.plannedHours);

  // The plan's bill rate per Labor Category, hours-weighted across its lines.
  const categoryBill = new Map<string, Micros>();
  {
    const acc = new Map<string, { rev: number; hours: number; firstRate: Micros }>();
    for (const s of lineStates) {
      const a = acc.get(s.line.laborCategoryId) ?? {
        rev: 0,
        hours: 0,
        firstRate: s.billRateMicros,
      };
      a.rev += moneyFor(s.billRateMicros, s.fullPlanHours);
      a.hours += s.fullPlanHours;
      acc.set(s.line.laborCategoryId, a);
    }
    for (const [id, a] of acc) {
      categoryBill.set(id, a.hours === 0 ? a.firstRate : divideRoundHalfUp(a.rev * 100, a.hours));
    }
  }

  // ── Actuals ──
  // Who each line is matched to. An unsplit line: its Suggested Resource, weighted by the
  // line's planned hours and costed at the line's rate. A split line (RP-24): each Share
  // holder, weighted by their own Share and costed at their own rate.
  interface Target {
    state: LineState;
    confirmed: boolean;
    weightFor: (month: string) => number;
    costFor: (month: string) => Micros;
  }
  const linesByResource = new Map<string, Target[]>();
  const addTarget = (resourceId: string, target: Target) => {
    const list = linesByResource.get(resourceId) ?? [];
    list.push(target);
    linesByResource.set(resourceId, list);
  };
  for (const s of lineStates) {
    const holders = s.line.shareHolders ?? [];
    if (holders.length > 0) {
      for (const h of holders) {
        const ownCost = parseCostByMonth(h.costByMonth);
        addTarget(h.resourceId, {
          state: s,
          confirmed: true,
          // Ramped, like an unsplit line's planned hours, so a Resource on both kinds of
          // line has their actuals split in one unit.
          weightFor: (month) =>
            divideRoundHalfUp(
              (h.enteredCentiByMonth.get(month) ?? 0) * (rampMilliByMonth.get(month) ?? 1000),
              1000,
            ),
          costFor: (month) => ownCost.get(month) ?? s.costRateMicros,
        });
      }
      continue;
    }
    if (s.line.resourceId === null) continue;
    addTarget(s.line.resourceId, {
      state: s,
      confirmed: s.line.confirmed,
      weightFor: (month) => s.plannedByMonth.get(month) ?? 0,
      costFor: (month) => lineCostFor(s, month),
    });
  }

  interface UnplannedState {
    resourceId: string;
    info: UnplannedResourceInput | undefined;
    billRateMicros: Micros | null;
    costRateMicros: Micros | null;
    resourceCostByMonth: Map<string, Micros>;
    rateSource: ActualsRateSource;
    accum: Accum;
    accumByMonth: Map<string, Accum>;
  }
  const unplanned = new Map<string, UnplannedState>();
  let outsideWindowHoursCenti = 0;

  function addActual(
    target: { accum: Accum; accumByMonth: Map<string, Accum> },
    month: string,
    hours: HoursCenti,
    billable: HoursCenti,
    billRate: Micros | null,
    costRate: Micros | null,
  ): void {
    const slice = emptyAccum();
    slice.actualHours = hours;
    slice.actualBillable = billable;
    slice.actualRevenue = billRate === null ? 0 : moneyFor(billRate, billable);
    slice.actualCost = costRate === null ? 0 : moneyFor(costRate, hours);
    addInto(target.accum, slice);
    const m = target.accumByMonth.get(month) ?? emptyAccum();
    addInto(m, slice);
    target.accumByMonth.set(month, m);
  }

  for (const a of input.actuals) {
    if (!inWindow.has(a.month)) {
      outsideWindowHoursCenti += a.hoursCenti;
      continue;
    }
    const candidates = linesByResource.get(a.resourceId) ?? [];
    const confirmed = candidates.filter((t) => t.confirmed);
    const targets = confirmed.length > 0 ? confirmed : candidates;

    if (targets.length > 0) {
      const weights = targets.map((t) => t.weightFor(a.month));
      const hoursParts = splitProportionally(a.hoursCenti, weights);
      const billableParts = splitProportionally(a.billableHoursCenti, weights);
      targets.forEach((t, i) => {
        addActual(
          t.state,
          a.month,
          hoursParts[i] ?? 0,
          billableParts[i] ?? 0,
          t.state.billRateMicros,
          t.costFor(a.month),
        );
      });
      continue;
    }

    let state = unplanned.get(a.resourceId);
    if (!state) {
      const info = input.unplannedResources.get(a.resourceId);
      const category = info?.laborCategory ?? null;
      let billRateMicros: Micros | null;
      let costRateMicros: Micros | null;
      let rateSource: ActualsRateSource;
      if (category !== null) {
        costRateMicros = parseMinor(category.avgInternalCost, 2) * MICROS_PER_RATE_CENT;
        const planRate = categoryBill.get(category.id);
        if (planRate !== undefined) {
          billRateMicros = planRate;
          rateSource = "plan-category";
        } else {
          billRateMicros = blendedBill;
          rateSource = blendedBill === null ? "unpriced" : "category-blended";
        }
      } else if (blendedBill !== null && blendedCost !== null) {
        billRateMicros = blendedBill;
        costRateMicros = blendedCost;
        rateSource = "blended";
      } else {
        billRateMicros = null;
        costRateMicros = null;
        rateSource = "unpriced";
      }
      state = {
        resourceId: a.resourceId,
        info,
        billRateMicros,
        costRateMicros,
        resourceCostByMonth: parseCostByMonth(info?.costByMonth),
        rateSource,
        accum: emptyAccum(),
        accumByMonth: new Map(),
      };
      unplanned.set(a.resourceId, state);
    }
    addActual(
      state,
      a.month,
      a.hoursCenti,
      a.billableHoursCenti,
      state.billRateMicros,
      state.resourceCostByMonth.get(a.month) ?? state.costRateMicros,
    );
  }

  // ── Shape ──
  const rows: PlanActualsRow[] = [];
  const total = emptyAccum();
  const monthTotals = new Map<string, Accum>(windowMonths.map((m) => [m, emptyAccum()]));

  const lastWindowMonth = windowMonths[windowMonths.length - 1];
  const resourcePricedInWindow = (byMonth: Map<string, Micros>): boolean =>
    windowMonths.some((m) => byMonth.has(m));

  for (const s of lineStates) {
    rows.push({
      key: s.line.id,
      kind: "line",
      lineId: s.line.id,
      laborCategoryId: s.line.laborCategoryId,
      laborCategoryName: s.line.laborCategoryName,
      resourceId: s.line.resourceId,
      resourceName: s.line.resourceName,
      rateSource: "line",
      billRateMicros: s.billRateMicros,
      costRateMicros:
        lastWindowMonth === undefined ? s.costRateMicros : lineCostFor(s, lastWindowMonth),
      // Planned figures span the whole plan, so any Resource-priced plan month counts.
      costSource:
        s.resourceCostByMonth.size > 0 && input.planMonths.some((m) => s.resourceCostByMonth.has(m))
          ? "resource"
          : "labor_category",
      ...compare(s.accum),
    });
    addInto(total, s.accum);
    for (const [month, a] of s.accumByMonth) {
      const t = monthTotals.get(month);
      if (t) addInto(t, a);
    }
  }

  const unplannedRows = [...unplanned.values()].sort((a, b) =>
    (a.info?.name ?? "").localeCompare(b.info?.name ?? ""),
  );
  for (const u of unplannedRows) {
    rows.push({
      key: `resource:${u.resourceId}`,
      kind: "unplanned",
      lineId: null,
      laborCategoryId: u.info?.laborCategory?.id ?? null,
      laborCategoryName: u.info?.laborCategory?.name ?? null,
      resourceId: u.resourceId,
      resourceName: u.info?.name ?? null,
      rateSource: u.rateSource,
      billRateMicros: u.billRateMicros,
      costRateMicros:
        lastWindowMonth === undefined
          ? u.costRateMicros
          : (u.resourceCostByMonth.get(lastWindowMonth) ?? u.costRateMicros),
      costSource: resourcePricedInWindow(u.resourceCostByMonth) ? "resource" : "labor_category",
      ...compare(u.accum),
    });
    addInto(total, u.accum);
    for (const [month, a] of u.accumByMonth) {
      const t = monthTotals.get(month);
      if (t) addInto(t, a);
    }
  }

  const contractedFeeMicros =
    input.contractedFee === null ? null : parseMinor(input.contractedFee, 2) * MICROS_PER_CENT;
  const fixedPriceBurn =
    input.pricingModel === "FixedPrice" && contractedFeeMicros !== null
      ? {
          contractedFeeMicros,
          actualCostMicros: total.actualCost,
          burnBp:
            contractedFeeMicros === 0
              ? null
              : divideRoundHalfUp(
                  divideRoundHalfUp(total.actualCost, MICROS_PER_CENT) * FULL_PERCENT_BP,
                  divideRoundHalfUp(contractedFeeMicros, MICROS_PER_CENT),
                ),
        }
      : null;

  return {
    windowMonths,
    includesPartialMonth,
    rows,
    byMonth: windowMonths.map((month) => ({
      month,
      ...compare(monthTotals.get(month) ?? emptyAccum()),
    })),
    summary: {
      ...compare(total),
      fullPlan: {
        hoursCenti: fullPlan.plannedHours,
        revenueMicros: fullPlan.plannedRevenue,
        costMicros: fullPlan.plannedCost,
        marginBp: marginOf(fullPlan.plannedRevenue, fullPlan.plannedCost),
      },
      outsideWindowHoursCenti,
      fixedPriceBurn,
    },
  };
}
